<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { type FormKitNode } from "@formkit/core";

interface BaseMembershipFields {
  "first-name": string;
  "last-name": string;
  account_tier: string | null;
  phone: string;
  password: string;
  terms: boolean;
  age: boolean;
  email_notifications: boolean;
  emergency_contact_name?: string;
  emergency_contact_relationship?: string;
  emergency_contact_number?: string;
  health_information?: string;
  dietary_requirements?: string;
}

interface IndividualMembershipFields extends BaseMembershipFields {
  account_type: "individual" | "lifetime";
  street1: string;
  street2?: string;
  suburb: string;
  city?: string;
  state?: string;
  country?: string;
  postcode: string;
}

interface CorporateMembershipFields extends BaseMembershipFields {
  account_type: "corporate";
  organisation_name: string;
  secondary_contact_name: string;
  secondary_contact_email: string;
  secondary_contact_phone: string;
  organisation_address_street1: string;
  organisation_address_street2: string;
  organisation_address_suburb: string;
  organisation_address_city: string;
  organisation_address_country: string;
  organisation_address_postcode: string;
}

export type MembershipFields = IndividualMembershipFields | CorporateMembershipFields;

const emits = defineEmits<{ submit: [any, FormKitNode] }>();

const props = withDefaults(defineProps<{ pending: boolean; initial?: Partial<MembershipFields> }>(), { initial: () => ({}) });

const values = ref<Partial<MembershipFields>>(props.initial);

const togglePassword = (node: FormKitNode) => {
  node.props.suffixIcon = node.props.suffixIcon === "eye" ? "eyeClosed" : "eye";
  node.props.type = node.props.type === "password" ? "text" : "password";
};

function submit(data: any, node: FormKitNode) {
  emits("submit", data, node);
}

const additionalInfoRequired = computed(
  () =>
    !!(
      values.value.emergency_contact_name ||
      values.value.emergency_contact_relationship ||
      values.value.emergency_contact_number ||
      values.value.health_information ||
      values.value.dietary_requirements
    )
);
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit v-model="values" type="form" :actions="false" @submit="submit">
    <div class="space-y-8">
      <slot name="preForm" />
      <FormKit
        id="password"
        type="password"
        name="password"
        label="Password"
        placeholder="Please enter a password"
        help="Must be at least 8 characters and include a mix of upper and lowercase characters and at least 1 number"
        validation="length:8|required|matches:/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/"
        :validation-messages="{
          required: 'Password is required',
          matches: 'Please make sure your password matches the requirements',
        }"
        prefix-icon="password"
        prefix-icon-class="text-xl"
        suffix-icon="eyeClosed"
        suffix-icon-class="hover:text-secondary text-xl"
        @suffix-icon-click="togglePassword"
      />
      <div class="space-y-2">
        <h4 class="text-center font-script text-2xl text-secondary lg:text-left xl:text-3xl">Personal Details</h4>
        <p class="text-sm text-gray-400">This information is required to register your account.</p>
      </div>
      <div class="flex flex-col gap-4 lg:flex-row">
        <div class="grow">
          <FormKit id="first-name" type="text" name="first-name" label="First Name" placeholder="First name" validation="required" />
        </div>
        <div class="grow">
          <FormKit id="last-name" type="text" name="last-name" label="Last Name" placeholder="Last name" validation="required" />
        </div>
      </div>
      <FormKit
        type="tel"
        name="phone"
        label="Phone number (mobile preferred)"
        placeholder="Please enter your phone number"
        validation="required"
        maxlength="15"
        validation-visibility="dirty"
      />
      <div class="space-y-8">
        <FormKit
          id="street1"
          type="text"
          name="street1"
          label="Street 1"
          placeholder="Enter your street address (optional)"
          validation="required"
        />
        <FormKit id="street2" type="text" name="street2" label="Street 2" placeholder="Enter your street address (optional)" />
        <div class="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <div class="grow">
            <FormKit id="suburb" type="text" name="suburb" label="Suburb" placeholder="Please enter your suburb" validation="required" />
          </div>
          <div class="grow">
            <FormKit
              id="postcode"
              type="text"
              name="postcode"
              label="Postcode"
              placeholder="Please enter your postcode"
              validation="required|numeric"
              maxlength="10"
            />
          </div>
        </div>
        <div class="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <div class="grow">
            <FormKit id="city" type="text" name="city" label="City" placeholder="Please enter your city (optional)" />
          </div>
          <div class="grow">
            <FormKit id="state" type="text" name="state" label="State" placeholder="Please enter your state (optional)" />
          </div>
        </div>
        <div class="space-y-2">
          <h4 class="text-center font-script text-2xl text-secondary lg:text-left xl:text-3xl">Additional Information</h4>
          <p class="text-sm text-gray-400">This information is optional, but will be required to attend some events.</p>
        </div>
        <div class="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <div class="grow">
            <FormKit
              id="emergency_contact_name"
              type="text"
              name="emergency_contact_name"
              label="Emergency Contact Name"
              placeholder="Enter emergency contact name"
              :validation="additionalInfoRequired ? 'required' : ''"
            />
          </div>
          <div class="grow">
            <FormKit
              id="emergency_contact_relationship"
              type="text"
              name="emergency_contact_relationship"
              label="Emergency Contact Relationship"
              placeholder="Enter relationship"
              :validation="additionalInfoRequired ? 'required' : ''"
            />
          </div>
        </div>
        <FormKit
          id="emergency_contact_number"
          type="text"
          name="emergency_contact_number"
          label="Emergency Contact Number"
          placeholder="Enter emergency contact number"
          :validation="additionalInfoRequired ? 'required' : ''"
        />
        <FormKit
          id="health_information"
          type="textarea"
          name="health_information"
          label="Health Information"
          placeholder="Enter any relevant health information"
          help="Please declare any severe allergies or serious injuries, medical conditions, essential medications (i.e. EpiPen, heart, asthma, diabetes, etc.), fitness or mobility issues that may affect your ability to participate. This information will enable Wildcare to keep you safe while volunteering"
          :validation="additionalInfoRequired ? 'required' : ''"
        />
        <FormKit
          id="dietary_requirements"
          type="textarea"
          name="dietary_requirements"
          label="Dietary Requirements"
          placeholder="Enter any dietary requirements"
          :validation="additionalInfoRequired ? 'required' : ''"
        />
      </div>
      <div v-if="values.account_type === 'corporate'" class="space-y-8">
        <div class="space-y-2">
          <h4 class="text-center font-script text-2xl text-secondary lg:text-left xl:text-3xl">Organisation Details</h4>
          <p class="text-sm text-gray-400">This information is required to register your organisation.</p>
        </div>
        <FormKit
          type="text"
          name="organisation_name"
          label="Organisation Name"
          placeholder="Enter your organisation's name"
          validation="required"
          validation-visibility="dirty"
        />
        <FormKit
          type="text"
          name="secondary_contact_name"
          label="Secondary Contact Name"
          placeholder="Enter the secondary contact's name"
          validation="required"
          validation-visibility="dirty"
        />
        <div class="flex flex-col gap-4 lg:flex-row">
          <div class="grow">
            <FormKit
              type="email"
              name="secondary_contact_email"
              label="Secondary Contact Email"
              placeholder="Enter the secondary contact's email"
              validation="required|email"
              validation-visibility="dirty"
            />
          </div>
          <div class="grow">
            <FormKit
              type="tel"
              name="secondary_contact_phone"
              label="Secondary Contact Phone"
              placeholder="Enter the secondary contact's phone number"
              validation="required"
              maxlength="15"
              validation-visibility="dirty"
            />
          </div>
        </div>
        <FormKit
          type="text"
          name="organisation_address_street1"
          label="Street Address 1"
          placeholder="Enter the street address"
          validation="required"
        />
        <FormKit type="text" name="organisation_address_street2" label="Street Address 2" placeholder="Enter the street address" />
        <div class="flex flex-col gap-4 lg:flex-row">
          <div class="grow">
            <FormKit type="text" name="organisation_address_suburb" label="Suburb" placeholder="Enter the suburb" validation="required" />
          </div>
          <div class="grow">
            <FormKit type="text" name="organisation_address_city" label="City" placeholder="Enter the city" validation="required" />
          </div>
        </div>
        <div class="flex flex-col gap-4 lg:flex-row">
          <div class="grow">
            <FormKit
              type="text"
              name="organisation_address_country"
              label="Country"
              placeholder="Enter the country"
              validation="required"
            />
          </div>
          <div class="grow">
            <FormKit
              type="text"
              name="organisation_address_postcode"
              label="Postcode"
              placeholder="Enter the postcode"
              validation="required|numeric"
              maxlength="10"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-7">
        <FormKit
          id="age"
          type="checkbox"
          name="age"
          validation="accepted"
          :validation-messages="{
            accepted: 'You must be over 18 to sign up',
          }"
          :value="false"
        >
          <template #label>
            <span class="font mt-0.5 select-none text-xl text-accentDark"> I am over 18 years old </span>
          </template>
        </FormKit>
        <FormKit
          id="email_notifications"
          type="checkbox"
          label="I'd like to receive news and marketing emails from Wildcare Tasmania"
          name="email_notifications"
          :value="true"
        />
        <slot name="postForm" />
      </div>
      <slot name="submit">
        <div class="flex items-center justify-end gap-5">
          <font-awesome-icon v-if="pending" class="text-primary" icon="fa-solid fa-spinner" size="2xl" spin-pulse></font-awesome-icon>
          <FormKit type="submit">Submit</FormKit>
        </div>
      </slot>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>
